<script setup lang="ts">
import { localStorageGet, localStorageSet, localStorageRemove } from '@mop/shared/utils/localStorage';
import SVGSearch from '@mop/shared/images/misc/search.svg?component';
import SearchItem from '@/views/search/SearchItem.vue';

defineOptions({
  name: 'MopHeaderSearchSuggestionsOverlay',
});

const { $mopI18n, $gtm, $urls } = useNuxtApp();

const router = useRouter();

const STORAGE_KEY = 'searchedTerms';
const STORAGE_LIMIT = 5;
const { searchContent, searchResultRef } = useMopSearch();
const { fetchAllJobs, getFilteredJobList } = useMopJob();
const SEARCH_DELAY = 300;
const INPUT_FOCUS_DELAY = 200;
const inputField: Ref<HTMLElement | undefined> = ref();
const cmsItemsRef = ref([]);
const searchInputRef: Ref<any> = ref('');
const hasResultRef = ref(false);
const hasJobResultRef = ref(false);
const resetValues = () => {
  cmsItemsRef.value = [];
};
const searchTermRef = ref(getSearchTermList());
let searchTimer: any = null;

function isValidMinLength() {
  return searchInputRef.value.length > 1;
}

// Search input and hint handling
const searchInputPlaceholderTextRef = computed(() => {
  return $mopI18n.t('locale.search.placeholder');
});

// Focus input field on overlay open
onMounted(async () => {
  setTimeout(() => {
    // @ts-ignore refs not part of context
    inputField.value.focus();
  }, INPUT_FOCUS_DELAY);

  await fetchAllJobs();
});

async function handleSearchSubmit() {
  if (!isValidMinLength()) {
    return;
  }
  handleAddSearchTerm();
  clearTimeout(searchTimer);
  $gtm.trackEvent({
    eventName: 'site_search_committed',
    parameters: [$gtm.getCurrentPageType(), searchInputRef.value],
  });
  // redirecting to root search
  await useMopOverlay().closeAll();
  router.push({
    path: $mopI18n.localePath($urls.SEARCH),
    query: {
      q: searchInputRef.value,
    },
  });
}

watch(searchInputRef, (value: string) => {
  clearTimeout(searchTimer);
  hasResultRef.value = true;
  hasJobResultRef.value = false;
  if (!isValidMinLength()) {
    resetValues();
    return;
  }

  searchTimer = setTimeout(async () => {
    await searchContent(value, 5);
    hasResultRef.value = searchResultRef.value.length > 0;
    hasJobResultRef.value = getFilteredJobList(value).length > 0;
    trackSearchResults(value);
  }, SEARCH_DELAY);
});

function trackSearchClick(itemType: string, item: any) {
  $gtm.trackEvent({
    eventName: 'site_search_clicked',
    parameters: [$gtm.getCurrentPageType(), searchInputRef, itemType, item],
  });
}
function trackSearchResults(searchTerm: string) {
  let searchResult = 'no_result';
  if (hasResultRef.value && hasJobResultRef.value) {
    searchResult = 'job_results page_results';
  } else if (hasResultRef.value) {
    searchResult = 'page_results';
  } else if (hasJobResultRef.value) {
    searchResult = 'job_results';
  }
  $gtm.trackEvent({
    eventName: 'site_search_results',
    parameters: [$gtm.getCurrentPageType(), searchTerm, searchResult],
  });
}

// Search term handling
function getSearchTermList() {
  try {
    const terms: string | null = localStorageGet(STORAGE_KEY);
    if (terms === null) {
      return [];
    }
    return JSON.parse(terms);
  } catch (error) {
    return [];
  }
}

function handleClearAllSearchTerms() {
  try {
    localStorageRemove(STORAGE_KEY);
    searchTermRef.value = getSearchTermList();
  } catch (error) {
    // do nothing
  }
}

function handleAddSearchTerm() {
  const searchValue: string = searchInputRef.value.trim();
  if (searchValue.length === 0) {
    return;
  }
  const termList: string[] = getSearchTermList();
  if (termList.length === 0) {
    searchTermRef.value = [searchValue];
    localStorageSet(STORAGE_KEY, JSON.stringify(searchTermRef.value));
    return;
  }
  try {
    if (termList.includes(searchValue)) {
      return;
    }
    searchTermRef.value = [searchValue, ...termList].splice(0, STORAGE_LIMIT);
    localStorageSet(STORAGE_KEY, JSON.stringify(searchTermRef.value));
  } catch (error) {
    // do nothing
  }
}

function onSearchInputKeydown(keyDownEvent: KeyboardEvent) {
  if (keyDownEvent.code === 'Escape') {
    searchInputRef.value = '';
  }
}
</script>

<template>
  <div class="search-suggest">
    <form action="#" class="search-suggest__form" @submit.prevent="handleSearchSubmit">
      <button type="submit" class="search-suggest__button">
        <SVGSearch width="30" height="19" />
      </button>
      <input
        ref="inputField"
        v-model="searchInputRef"
        class="search-suggest__search-input"
        autocomplete="off"
        spellcheck="false"
        :placeholder="searchInputPlaceholderTextRef"
        @blur="handleAddSearchTerm"
        @keydown="onSearchInputKeydown"
      />
    </form>
    <div v-if="!searchInputRef && searchTermRef.length" class="search-suggest__searched-terms">
      <span v-for="(searchTerm, index) in searchTermRef" :key="index" class="search-suggest__searched-term-wrap">
        <a class="search-suggest__searched-term" href="#" @click.prevent="searchInputRef = searchTerm">
          {{ searchTerm }}
        </a>
      </span>
      <span class="search-suggest__searched-term-wrap search-suggest__searched-term-wrap--transparent">
        <button class="search-suggest__searched-terms-clear" @click="handleClearAllSearchTerms">
          {{ $mopI18n.t('locale.search.clear') }}
        </button>
      </span>
    </div>
    <div class="search-suggest__scroll">
      <div v-if="searchResultRef.length > 0 || hasJobResultRef" class="search-suggest__result">
        <template v-if="hasJobResultRef">
          <div
            class="search-suggest__item search-suggest__item--job"
            :style="{ '--bg-item-color': $mopConfig.getJobSearchBgColor() }"
          >
            <div class="search-suggest__jobs-found">
              {{ $mopI18n.t('locale.search.jobs_found') }}
            </div>
            <NuxtLink
              class="search-suggest__job-search"
              :to="$mopI18n.localePath(`${$urls.CAREER_PAGE}?search=${searchInputRef}`)"
              :style="{ '--bg-color': $mopConfig.getHeaderJobButtonColor() }"
              no-prefetch
              @click="
                trackSearchClick('job_results', $mopI18n.localePath(`${$urls.CAREER_PAGE}?search=${searchInputRef}`))
              "
            >
              {{ $mopI18n.t('locale.jobs.search_button') }}
            </NuxtLink>
          </div>
        </template>
        <template v-if="searchResultRef.length">
          <SearchItem
            v-for="item in searchResultRef"
            :key="item.title"
            :title="item.title"
            :description="item.description"
            :link="item.link"
            class="search-suggest__item"
            @click="trackSearchClick('page_results', item.link)"
          />
          <div class="search-suggest__show-all">
            <button class="search-suggest__show-all-button" @click="handleSearchSubmit">
              {{ $mopI18n.t('locale.search.show_all') }}
            </button>
          </div>
        </template>
      </div>
      <div v-else-if="isValidMinLength() && !hasResultRef && !hasJobResultRef" class="search-suggest__no-result">
        <div class="search-suggest__no-result-info">
          {{ $mopI18n.t(`locale.search.empty`, [searchInputRef.toUpperCase()]) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.search-suggest__result {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: 'row';

  @include apply-upto(small) {
    padding: 0;
  }
}

.search-suggest__item {
  border-bottom: 1px solid $black;
  padding: $space20 $global-padding $space10;
}

.search-suggest__form {
  display: flex;
  padding: $space5 0;
  border-bottom: 1px solid $black;

  @include apply-upto(small) {
    padding: $space2 $global-padding;
  }
}

.search-suggest__button {
  border: 0;
  background: transparent;
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @include apply-upto(small) {
    width: 22px;
  }
}

.search-suggest__search-input {
  @include text-style(headline-condensed);

  position: absolute;
  right: $global-padding;
  left: 46px; // 30px space between search icon and input, 16px width of icon
  height: 40px;
  margin: 0;
  padding: $space10 0;
  border: 0;
  text-transform: uppercase;
  background: transparent;
}

.search-suggest__search-input::-ms-clear {
  display: none;
}

.search-suggest__search-input::placeholder {
  color: $black;
}

.search-suggest__search-input:focus {
  outline: none;
}

.search-suggest__no-result {
  padding: 0;
  flex-direction: column;
}

.search-suggest__no-result-info {
  padding: $space30 $global-padding $space30 46px; // aligning with search text, 16px padding + 16px icon
}

.search-suggest__searched-terms {
  width: 100%;
  padding: $space20 0; // aligning with search text, 16px padding + 16px icon
}

.search-suggest__searched-term-wrap {
  display: flex;
  align-items: center;
  padding: $space5 0 $space5 46px; // aligning with search text, 16px padding + 16px icon

  @include hover {
    background: $light-grey;
    cursor: pointer;
  }
}

.search-suggest__searched-term-wrap--transparent {
  background: transparent;

  @include hover {
    background: transparent;
  }
}

.search-suggest__searched-term {
  @include text-style(strong);

  width: columns(7);
  margin-right: columns(1);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-transform: uppercase;

  @include apply-only(large) {
    width: columns(12);
  }

  @include apply-only(medium) {
    width: columns(14);
  }

  @include apply-upto(small) {
    width: columns(34);
  }
}

.search-suggest__show-all {
  padding: $space10 $global-padding;
}

.search-suggest__show-all-button,
.search-suggest__searched-terms-clear {
  @include link-underlined();
  @include text-style(common);
}

.search-suggest__scroll {
  overflow-y: auto;
  max-height: calc(#{$ui-viewport-height} - (#{$sticky-big-header-height} + #{$search-form-height}));
  max-height: calc(#{$ui-viewport-height} - (#{$sticky-big-header-height} + #{$search-form-height}));

  @include apply-upto(medium) {
    max-height: calc(#{$ui-viewport-height} - (#{$sticky-small-header-height} + #{$search-form-height-small}));
    max-height: calc(#{$ui-viewport-height} - (#{$sticky-small-header-height} + #{$search-form-height-small}));
  }
}

.search-suggest__job-search {
  @include text-style(strong);

  background: var(--bg-color);
  color: $white;
  text-transform: uppercase;
  padding: 8px $space15;
  margin-right: $space30;
  align-self: center;
}

.search-suggest__item--job {
  background: var(--bg-item-color);
  padding-bottom: $space20;
  border-bottom: 0;
}

.search-suggest__jobs-found {
  @include text-style(strong);

  font-size: 22px;
  line-height: 22px;
  color: $white;
  text-transform: uppercase;
  margin-bottom: $space20;
}
</style>
